import { useRef, useState } from 'react';
import { endpoints } from '@/api/endpoints';
import { useApi } from '@/api/useApi';
import { DataTable, type Column } from '@/components/DataTable';
import { useLanguage } from '@/i18n/LanguageContext';
import { formatDate, formatNumber } from '@/i18n/format';
import type { Seller } from '@/api/types';
import { RequirePermission } from '@/components/RequirePermission';
import { ErrorBanner } from '@/components/ErrorBanner';
import { CodeField, codePayload, type CodeValue } from '@/components/CodeField';
import { describeSetupError, newKey } from '@/pages/Warehouses/setup';

const LIMIT = 50;

export function SellersListPage(): JSX.Element {
  const [offset, setOffset] = useState(0);
  const [activeOnly, setActiveOnly] = useState(false);
  const result = useApi(() => endpoints.sellers({ activeOnly, limit: LIMIT, offset }), [activeOnly, offset]);
  const { t, locale } = useLanguage();
  const [adding, setAdding] = useState(false);
  const [createdNote, setCreatedNote] = useState<string | null>(null);

  const columns: readonly Column<Seller>[] = [
    { key: 'name', header: t('inbounds.colSeller'), render: (r) => r.name },
    { key: 'legal_entity_name', header: t('billing.legalEntity'), render: (r) => r.legal_entity_name ?? '—' },
    { key: 'active', header: t('common.status'), render: (r) => (r.active ? t('sellers.active') : t('sellers.inactive')) },
    { key: 'stock_sku_count', header: t('sellers.stockSkus'), render: (r) => formatNumber(r.stock_sku_count, locale), align: 'right' },
    { key: 'open_orders', header: t('sellers.openOrders'), render: (r) => formatNumber(r.open_orders, locale), align: 'right' },
    { key: 'open_discrepancies', header: t('inbounds.colDiscrepancies'), render: (r) => formatNumber(r.open_discrepancies, locale), align: 'right' },
    { key: 'tariff_name', header: t('billing.tariff'), render: (r) => r.tariff_name ?? t('common.none') },
    { key: 'last_activity', header: t('sellers.lastActivity'), render: (r) => formatDate(r.last_activity, locale) },
  ];

  return (
    <div>
      <div className="page-header">
        <h1>{t('nav.sellers')}</h1>
        <RequirePermission permission="ORG.WRITE">
          {!adding && (
            <button type="button" className="primary" onClick={() => { setCreatedNote(null); setAdding(true); }}>
              {t('sellers.create')}
            </button>
          )}
        </RequirePermission>
      </div>
      {createdNote && <p className="ok-note" role="status">{createdNote}</p>}
      {adding && (
        <CreateSellerForm
          onCancel={() => setAdding(false)}
          onCreated={(note) => {
            setAdding(false);
            setCreatedNote(note);
            result.reload();
          }}
        />
      )}
      <div className="filters">
        <label>
          <input
            type="checkbox"
            checked={activeOnly}
            onChange={(e) => {
              setActiveOnly(e.target.checked);
              setOffset(0);
            }}
          />
          {t('sellers.activeOnly')}
        </label>
      </div>
      <DataTable
        result={result}
        columns={columns}
        getRowHref={(r) => `/sellers/${r.id}`}
        limit={LIMIT}
        offset={offset}
        onPageChange={setOffset}
        emptyTitle={t('sellers.emptyTitle')}
      />
    </div>
  );
}

/** ADR-083 - add a seller after onboarding, with the same code guidance as everywhere else. */
function CreateSellerForm({ onCancel, onCreated }: { onCancel: () => void; onCreated: (note: string) => void }): JSX.Element {
  const { t } = useLanguage();
  const [name, setName] = useState('');
  const [code, setCode] = useState<CodeValue>({ code: '', auto: true });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const key = useRef(newKey());
  const errText = error ? describeSetupError(t, error) : null;
  return (
    <form
      className="card wh-form"
      aria-labelledby="seller-create-title"
      onSubmit={(e) => {
        e.preventDefault();
        setBusy(true);
        setError(null);
        endpoints
          .createSellerOwner({ name: name.trim(), ...codePayload(code), idempotencyKey: key.current })
          .then((r) => {
            key.current = newKey();
            onCreated(t('sellers.created', { name: name.trim(), code: r.code }));
          }, setError)
          .finally(() => setBusy(false));
      }}
    >
      <h2 id="seller-create-title" className="card-title">{t('sellers.createTitle')}</h2>
      <div className="form-grid">
        <div className="field-row">
          <label htmlFor="seller-new-name">{t('sellers.name')}</label>
          <input id="seller-new-name" value={name} onChange={(e) => setName(e.target.value)} required maxLength={200} disabled={busy} />
        </div>
        <CodeField entity="SELLER" id="seller-new-code" name={name} value={code} onChange={setCode} disabled={busy} />
      </div>
      {error !== null && (errText ? <div className="error-banner" role="alert">{errText}</div> : <ErrorBanner error={error} />)}
      <div className="form-actions">
        <button type="submit" className="primary" disabled={busy || name.trim() === ''}>
          {t('sellers.createSubmit')}
        </button>
        <button type="button" onClick={onCancel} disabled={busy}>
          {t('common.cancel')}
        </button>
      </div>
    </form>
  );
}
